import { create } from 'zustand';
import { AnalysisCancelledError } from '@/workers/analysisClient';
import {
  ImportError,
  startImport,
  type ImportFileInfo,
  type ImportHandle,
  type ImportOutcome,
  type ImportStep,
} from './importSong';

export type ImportStatus = 'idle' | 'running' | 'done' | 'cancelled' | 'error';

/** Where the song comes from: a file analysed here, or a link analysed by a server. */
export type ImportSource = 'file' | 'youtube';

/** Steps of a server-side analysis (the YouTube module). */
export type RemoteStage = 'queued' | 'downloading' | 'analysing';

interface ImportState {
  status: ImportStatus;
  source: ImportSource;
  file: File | null;
  info: ImportFileInfo | null;
  step: ImportStep;
  fraction: number;
  /** The BTC model ran (its step is shown, before tuning). */
  usedModel: boolean;
  remoteStage: RemoteStage;
  /** Jobs ahead in the server's queue. */
  queuePosition: number;
  outcome: ImportOutcome | null;
  error: string | null;
  start: (file: File) => Promise<void>;
  /**
   * Runs an import from another source (the YouTube module) through the same progress screen.
   * `begin` gets setters for progress; `retry` runs it again.
   */
  startRemote: (
    info: ImportFileInfo,
    begin: (progress: {
      stage: (stage: RemoteStage, position: number) => void;
      info: (info: Partial<ImportFileInfo>) => void;
    }) => ImportHandle,
  ) => Promise<void>;
  cancel: () => void;
  restart: () => void;
}

let handle: ImportHandle | null = null;
let activeToken: symbol | null = null;
let retry: (() => void) | null = null;

/** Error messages from other sources are already written for people. */
const isMessage = (e: unknown): e is Error & { code: string } =>
  e instanceof Error && 'code' in e && typeof e.code === 'string';

export const useImportStore = create<ImportState>()((set, get) => {
  async function track(token: symbol, h: ImportHandle) {
    handle = h;
    try {
      const outcome = await h.outcome;
      if (activeToken === token) set({ status: 'done', outcome, fraction: 1 });
    } catch (error) {
      if (activeToken !== token) return;
      if (error instanceof AnalysisCancelledError || (error as Error).name === 'AbortError')
        set({ status: 'cancelled' });
      else if (error instanceof ImportError || isMessage(error))
        set({ status: 'error', error: error.message });
      else {
        console.error(error);
        set({ status: 'error', error: 'Something went wrong while analyzing this song.' });
      }
    }
  }

  return {
    status: 'idle',
    source: 'file',
    file: null,
    info: null,
    step: 'decoding',
    fraction: 0,
    usedModel: false,
    remoteStage: 'queued',
    queuePosition: 0,
    outcome: null,
    error: null,

    async start(file) {
      handle?.cancel();
      const token = Symbol('import');
      activeToken = token;
      const isActive = () => activeToken === token;
      retry = () => void get().start(file);
      // Set synchronously: the caller navigates to the Analyzing page right away.
      set({
        status: 'running',
        source: 'file',
        file,
        info: { songId: '', fileName: file.name, fileSize: file.size },
        step: 'decoding',
        fraction: 0,
        usedModel: false,
        outcome: null,
        error: null,
      });
      // Callbacks can fire before startImport returns, so they check the token, not the handle.
      await track(
        token,
        startImport(file, {
          onProgress: ({ step, fraction }) => {
            if (isActive())
              set(step === 'model' ? { step, fraction, usedModel: true } : { step, fraction });
          },
          onFileInfo: (info) => {
            if (isActive()) set({ info });
          },
        }),
      );
    },

    async startRemote(info, begin) {
      handle?.cancel();
      const token = Symbol('import');
      activeToken = token;
      const isActive = () => activeToken === token;
      retry = () => void get().startRemote(info, begin);
      set({
        status: 'running',
        source: 'youtube',
        file: null,
        info,
        remoteStage: 'queued',
        queuePosition: 0,
        fraction: 0,
        outcome: null,
        error: null,
      });
      await track(
        token,
        begin({
          stage: (remoteStage, queuePosition) => {
            if (isActive()) set({ remoteStage, queuePosition });
          },
          info: (patch) => {
            const current = get().info;
            if (isActive() && current) set({ info: { ...current, ...patch } });
          },
        }),
      );
    },

    cancel() {
      handle?.cancel();
    },

    restart() {
      retry?.();
    },
  };
});
